// Planification du façonnage : regroupe les étapes par réglage pour limiter les recalages,
// sans dépasser les échéances, en respectant les feuilles disponibles, les verrous et les horaires.
import type { Machine, TaskWithJob, WorkingHours } from "../data/types.ts";
import { addWorkingMinutes, nextWorkingInstant } from "./time.ts";
import { passes, setupParams, specConflict } from "../data/catalog.ts";

export interface Slot {
  task_id: string;
  machine_id: string;
  start: Date;
  end: Date;
  setup_min: number;
  run_min: number;
}

export interface PlanMetrics {
  recalages: number;
  setup_min: number;
  late: string[];
  end: Date | null;
}

export interface PlanResult {
  slots: Slot[];
  unplaced: { task_id: string; reason: string }[];
  metrics: PlanMetrics;
  /** Même affectation, ordre d'arrivée des jobs : pour l'avant / après. */
  baseline: PlanMetrics;
}

export const ACTIVE_STATUSES = ["calage", "production", "pause"] as const;

export function setupKey(t: Pick<TaskWithJob, "operation" | "fold_type" | "format_in" | "format_out"> & Partial<Pick<TaskWithJob, "params">>): string {
  const base = [t.operation, t.fold_type || "", t.format_in, t.format_out].join("|");
  const extra = setupParams({ operation: t.operation, params: t.params || {} });
  return extra ? `${base}|${extra}` : base;
}

type CompatTask = Pick<TaskWithJob, "operation" | "format_in"> & Partial<Pick<TaskWithJob, "params" | "grammage" | "pages">>;

/** Raison pour laquelle la machine ne peut pas faire l'étape (null = compatible). */
export function incompatibility(m: Machine, t: CompatTask): string | null {
  if (!m.active) return "machine désactivée";
  if (!m.operations.includes(t.operation)) return "opération non paramétrée sur la machine";
  if (m.formats.length && !m.formats.includes(t.format_in)) return `format ${t.format_in} non accepté`;
  return specConflict(m.specs, { operation: t.operation, params: t.params || {}, grammage: t.grammage ?? null, pages: t.pages ?? null });
}

export function isCompatible(m: Machine, t: CompatTask): boolean {
  return incompatibility(m, t) === null;
}

export function runMinutes(t: TaskWithJob, m: Machine): number {
  if (t.manual_run_min != null && t.manual_run_min > 0) return t.manual_run_min;
  const speed = Math.max(1, m.speed_per_hour || 1);
  const remaining = Math.max(0, t.job.quantity - (t.produced_qty || 0));
  return Math.max(5, Math.ceil((remaining / speed) * 60 * passes(m.specs, t)));
}

function setupMinutes(prevKey: string | null, t: TaskWithJob, m: Machine): number {
  return prevKey === setupKey(t) ? 0 : Math.max(0, m.setup_min);
}

export interface Block { start: Date; end: Date; key: string }

/** Enchaîne des étapes dans l'ordre donné sur une machine, en contournant les blocs fixes. */
export type ReadyFn = (t: TaskWithJob) => Date | null;
export const sheetsReady: ReadyFn = (t) => (t.job.sheets_ready_at ? new Date(t.job.sheets_ready_at) : null);

export function sequence(
  m: Machine, ordered: TaskWithJob[], from: Date, prevKey: string | null, hours: WorkingHours, fixed: Block[] = [],
  readyOf: ReadyFn = sheetsReady,
): Slot[] {
  const slots: Slot[] = [];
  let cursor = nextWorkingInstant(from, hours);
  let last = prevKey;
  const blocks = [...fixed].sort((a, b) => a.start.getTime() - b.start.getTime());
  for (const t of ordered) {
    const ready = readyOf(t);
    if (ready && ready > cursor) cursor = nextWorkingInstant(ready, hours);
    for (let guard = 0; guard < 50; guard++) {
      const setup = setupMinutes(last, t, m);
      const run = runMinutes(t, m);
      const start = cursor;
      const end = addWorkingMinutes(start, setup + run, hours);
      const clash = blocks.find((b) => end > b.start && start < b.end);
      if (!clash) {
        slots.push({ task_id: t.id, machine_id: m.id, start, end, setup_min: setup, run_min: run });
        cursor = end;
        last = setupKey(t);
        break;
      }
      cursor = nextWorkingInstant(clash.end, hours);
      last = clash.key;
    }
  }
  return slots;
}

function metricsOf(slots: Slot[], byId: Map<string, TaskWithJob>): PlanMetrics {
  let end: Date | null = null;
  const late: string[] = [];
  for (const s of slots) {
    if (!end || s.end > end) end = s.end;
    const t = byId.get(s.task_id);
    if (t && s.end > new Date(t.job.due_at)) late.push(s.task_id);
  }
  return {
    recalages: slots.filter((s) => s.setup_min > 0).length,
    setup_min: slots.reduce((a, s) => a + s.setup_min, 0),
    late,
    end,
  };
}

function due(t: TaskWithJob): number {
  const d = new Date(t.job.due_at).getTime();
  return t.job.priority === "urgent" ? d - 7 * 86_400_000 : d; // l'urgent passe devant à échéance égale
}

/** Ordonne les étapes d'une machine : on garde le même réglage tant que l'échéance la plus serrée tient. */
function orderForMachine(
  m: Machine, tasks: TaskWithJob[], from: Date, prevKey: string | null, hours: WorkingHours, readyOf: ReadyFn,
): TaskWithJob[] {
  const remaining = [...tasks];
  const out: TaskWithJob[] = [];
  let cursor = nextWorkingInstant(from, hours);
  let last = prevKey;
  const isReady = (t: TaskWithJob) => { const r = readyOf(t); return !r || r <= cursor; };
  while (remaining.length) {
    let ready = remaining.filter(isReady);
    if (!ready.length) {
      const soonest = Math.min(...remaining.map((t) => readyOf(t)!.getTime()));
      cursor = nextWorkingInstant(new Date(soonest), hours);
      ready = remaining.filter(isReady);
    }
    ready.sort((a, b) => due(a) - due(b));
    const urgent = ready[0];
    let pick = urgent;
    const same = ready.filter((t) => setupKey(t) === last).sort((a, b) => due(a) - due(b))[0];
    if (same && same !== urgent) {
      // on garde le réglage si l'étape la plus urgente tient toujours son échéance après
      const afterSame = addWorkingMinutes(cursor, runMinutes(same, m), hours);
      const urgentEnd = addWorkingMinutes(afterSame, m.setup_min + runMinutes(urgent, m), hours);
      if (urgentEnd <= new Date(urgent.job.due_at)) pick = same;
    } else if (!same) {
      // nouveau réglage : on choisit, parmi les étapes dont l'échéance le permet, le groupe le plus gros
      const groups = new Map<string, TaskWithJob[]>();
      for (const t of ready) groups.set(setupKey(t), [...(groups.get(setupKey(t)) || []), t]);
      const urgentEndIfFirst = addWorkingMinutes(cursor, m.setup_min + runMinutes(urgent, m), hours);
      if (urgentEndIfFirst <= new Date(urgent.job.due_at)) {
        let best = urgent;
        let bestSize = (groups.get(setupKey(urgent)) || []).length;
        for (const [, g] of groups) {
          const lead = g[0];
          if (g.length <= bestSize || lead === urgent) continue;
          const groupMin = g.reduce((a, t) => a + runMinutes(t, m), m.setup_min);
          const urgentEnd = addWorkingMinutes(cursor, groupMin + m.setup_min + runMinutes(urgent, m), hours);
          if (urgentEnd <= new Date(urgent.job.due_at)) { best = lead; bestSize = g.length; }
        }
        pick = best;
      }
    }
    out.push(pick);
    remaining.splice(remaining.indexOf(pick), 1);
    cursor = addWorkingMinutes(cursor, setupMinutes(last, pick, m) + runMinutes(pick, m), hours);
    last = setupKey(pick);
  }
  return out;
}

export interface OptimizeInput {
  machines: Machine[];
  tasks: TaskWithJob[];
  hours: WorkingHours;
  now: Date;
}

/** Planifie toutes les étapes non démarrées et non verrouillées.
 *  Les étapes d'un même job s'enchaînent : l'encartage attend la fin du pliage. */
export function optimize({ machines, tasks, hours, now }: OptimizeInput): PlanResult {
  const active = machines.filter((m) => m.active).sort((a, b) => a.position - b.position);
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const open = tasks.filter((t) => t.status !== "termine" && t.job.status !== "annule" && t.job.status !== "termine");
  const start = nextWorkingInstant(now, hours);

  // Fin connue (ou prévue) de chaque étape qui ne sera pas replanifiée
  const knownEnd = new Map<string, Date | null>();
  for (const t of tasks) {
    if (t.status === "termine") knownEnd.set(t.id, null); // déjà fait : ne retarde rien
  }

  // 1. Blocs fixes : étapes en cours et étapes verrouillées
  const fixed = new Map<string, Block[]>();
  const tail = new Map<string, { at: Date; key: string | null }>();
  for (const m of active) { fixed.set(m.id, []); tail.set(m.id, { at: start, key: null }); }
  for (const t of open) {
    if (!t.machine_id || !fixed.has(t.machine_id)) continue;
    const m = active.find((x) => x.id === t.machine_id)!;
    if ((ACTIVE_STATUSES as readonly string[]).includes(t.status)) {
      const end = addWorkingMinutes(now, runMinutes(t, m), hours);
      fixed.get(m.id)!.push({ start: new Date(now.getTime() - 1), end, key: setupKey(t) });
      knownEnd.set(t.id, end);
      const tl = tail.get(m.id)!;
      if (end > tl.at) tail.set(m.id, { at: end, key: setupKey(t) });
    } else if (t.locked && t.planned_start && t.planned_end) {
      fixed.get(m.id)!.push({ start: new Date(t.planned_start), end: new Date(t.planned_end), key: setupKey(t) });
      knownEnd.set(t.id, new Date(t.planned_end));
    }
  }

  const toPlan = open.filter((t) => (t.status === "a_planifier" || t.status === "planifie") && !knownEnd.has(t.id));
  const siblings = new Map<string, TaskWithJob[]>();
  for (const t of tasks) siblings.set(t.job_id, [...(siblings.get(t.job_id) || []), t]);
  const unplaced: PlanResult["unplaced"] = [];

  const run = (fifo: boolean) => {
    const ends = new Map(knownEnd);
    const tails = new Map([...tail].map(([k, v]) => [k, { ...v }]));
    const out: Slot[] = [];
    const skipped: PlanResult["unplaced"] = [];
    const readyOf: ReadyFn = (t) => {
      let r = sheetsReady(t);
      for (const p of siblings.get(t.job_id) || []) {
        if (p.seq >= t.seq) continue;
        const e = ends.get(p.id);
        if (e && (!r || e > r)) r = e;
      }
      return r;
    };
    const levels = [...new Set(toPlan.map((t) => t.seq))].sort((a, b) => a - b);
    for (const level of levels) {
      const batch = toPlan.filter((t) => t.seq === level).sort((a, b) => due(a) - due(b));
      // une étape dont la précédente n'a pas pu être planifiée attend
      const blocked = batch.filter((t) => (siblings.get(t.job_id) || []).some((p) => p.seq < t.seq && !ends.has(p.id)));
      for (const t of blocked) skipped.push({ task_id: t.id, reason: "L'étape précédente n'est pas planifiée" });
      const ready = batch.filter((t) => !blocked.includes(t));

      // Affectation : par échéance, sur la machine compatible qui finit le plus tôt (bonus si même réglage)
      const load = new Map(active.map((m) => [m.id, 0]));
      const keys = new Map(active.map((m) => [m.id, new Set<string>(tails.get(m.id)!.key ? [tails.get(m.id)!.key!] : [])]));
      const assigned = new Map(active.map((m) => [m.id, [] as TaskWithJob[]]));
      for (const t of ready) {
        const cands = active.filter((m) => isCompatible(m, t));
        if (!cands.length) { skipped.push({ task_id: t.id, reason: "Aucune machine compatible" }); continue; }
        let best = cands[0], bestScore = Infinity;
        for (const m of cands) {
          const base = Math.max(0, (tails.get(m.id)!.at.getTime() - start.getTime()) / 60_000);
          const setup = keys.get(m.id)!.has(setupKey(t)) ? 0 : m.setup_min;
          const score = base + load.get(m.id)! + setup + runMinutes(t, m);
          if (score < bestScore) { best = m; bestScore = score; }
        }
        assigned.get(best.id)!.push(t);
        load.set(best.id, load.get(best.id)! + (keys.get(best.id)!.has(setupKey(t)) ? 0 : best.setup_min) + runMinutes(t, best));
        keys.get(best.id)!.add(setupKey(t));
      }
      // Ordre et horaires, machine par machine
      for (const m of active) {
        const list = assigned.get(m.id)!;
        if (!list.length) continue;
        const tl = tails.get(m.id)!;
        const blocks = fixed.get(m.id)!.filter((b) => b.end > tl.at);
        const ordered = fifo
          ? [...list].sort((a, b) => a.job.created_at.localeCompare(b.job.created_at) || a.seq - b.seq)
          : orderForMachine(m, list, tl.at, tl.key, hours, readyOf);
        const slots = sequence(m, ordered, tl.at, tl.key, hours, blocks, readyOf);
        for (const s of slots) ends.set(s.task_id, s.end);
        out.push(...slots);
        const last = slots[slots.length - 1];
        if (last) tails.set(m.id, { at: last.end, key: setupKey(byId.get(last.task_id)!) });
      }
    }
    return { slots: out, skipped };
  };

  const best = run(false);
  const fifo = run(true);
  unplaced.push(...best.skipped);
  return { slots: best.slots, unplaced, metrics: metricsOf(best.slots, byId), baseline: metricsOf(fifo.slots, byId) };
}

/** Recalcule les horaires d'une machine dans l'ordre donné (après un glisser-déposer). */
export function reflow(m: Machine, ordered: TaskWithJob[], from: Date, hours: WorkingHours): Slot[] {
  return sequence(m, ordered, from, null, hours);
}

export interface Alert { task_id: string; kind: "retard" | "incompatible" | "feuilles" | "ordre"; message: string }

export function alertsFor(tasks: TaskWithJob[], machines: Machine[]): Alert[] {
  const out: Alert[] = [];
  const byId = new Map(machines.map((m) => [m.id, m]));
  for (const t of tasks) {
    if (t.planned_start && t.status !== "termine") {
      const before = tasks.find((p) => p.job_id === t.job_id && p.seq === t.seq - 1 && p.status !== "termine" && p.planned_end);
      if (before && new Date(before.planned_end!) > new Date(t.planned_start)) {
        out.push({ task_id: t.id, kind: "ordre", message: "Commence avant la fin de l'étape précédente" });
      }
    }
    if (!t.machine_id || t.status === "termine") continue;
    const m = byId.get(t.machine_id);
    const why = m ? incompatibility(m, t) : null;
    if (m && why) {
      out.push({ task_id: t.id, kind: "incompatible", message: `${m.name} ne convient pas : ${why}` });
    }
    if (t.planned_end && new Date(t.planned_end) > new Date(t.job.due_at)) {
      out.push({ task_id: t.id, kind: "retard", message: "Finit après l'échéance" });
    }
    if (t.planned_start && t.job.sheets_ready_at && new Date(t.planned_start) < new Date(t.job.sheets_ready_at)) {
      out.push({ task_id: t.id, kind: "feuilles", message: "Planifié avant l'arrivée des feuilles" });
    }
  }
  return out;
}
